// AppContextMenu - right-click menu for app cards (grid and list views)
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { navigationManager } from '../../../managers/navigation-manager';
import { RevoltIcon } from '../../../components/RevoltIcon';
import { canShowRemixButton } from '../../../core/utils/appAccessUtils';
import './AppContextMenu.css';

export function AppContextMenu({
  anchorX,
  anchorY,
  project,
  onClose,
  onOpenWorkspace,
  onToggleFavorite,
  onLeaveApp,
  onRemixApp,
  onDeleteApp,
  easyAppManagementEnabled,
}) {
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({ x: anchorX, y: anchorY });

  const isOwner = !!project?.isOwner;
  const isFavorited = !!project?.isFavorited;
  const isMember = !!project?.membershipRole; // Any membership role
  const canRemix = canShowRemixButton(project, isMember);
  const ownerName = project?.ownerName;

  // Viewport-fixed menu (portaled as sibling of backdrop); position via translate3d (see file comment on div).
  useLayoutEffect(() => {
    const padding = 12;
    const menuWidth = menuRef.current?.offsetWidth || 240;
    const menuHeight = menuRef.current?.offsetHeight || 220;
    const vw = typeof window !== 'undefined' ? window.innerWidth : menuWidth + padding * 2;
    const vh = typeof window !== 'undefined' ? window.innerHeight : menuHeight + padding * 2;

    const baseX = anchorX ?? 0;
    const baseY = anchorY ?? 0;

    const maxX = Math.max(padding, vw - menuWidth - padding);
    const maxY = Math.max(padding, vh - menuHeight - padding);

    const safeX = Math.min(Math.max(baseX, padding), maxX);
    const safeY = Math.min(Math.max(baseY, padding), maxY);

    setCoords({ x: safeX, y: safeY });
  }, [anchorX, anchorY]);

  // Close on escape or scroll
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = () => onClose();

    window.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  const handleAction = (action) => {
    action();
    onClose();
  };

  const favoriteLabel = isFavorited ? 'Remove favorite' : 'Add to favorites';
  const favoriteIcon = isFavorited ? 'fa-solid fa-star' : 'fa-regular fa-star';
  const ownerLabel = isOwner ? 'Owned by you' : `Shared by ${ownerName || 'Unknown'}`;

  if (!project) {
    return null;
  }

  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  const menuTree = (
    <>
      <div className="app-context-menu-overlay" onClick={onClose} aria-hidden />
      <div
        className="app-context-menu"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          // translate3d: Chromium/Electron can ignore left/top on position:fixed when the same
          // node uses backdrop-filter (see .app-context-menu CSS — blur moved off this shell).
          transform: `translate3d(${coords.x}px, ${coords.y}px, 0)`,
          zIndex: 5001,
        }}
        role="menu"
        aria-label="Project actions"
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-context-menu__header">
          <div className="app-context-menu__title">{project.name || 'Project'}</div>
          <div className="app-context-menu__meta">{ownerLabel}</div>
        </div>

        <div className="app-context-menu__list">
          {canRemix && (
            <button
              className="app-context-menu__item app-context-menu__item--remix"
              role="menuitem"
              onClick={() => handleAction(() => onRemixApp && onRemixApp(project))}
            >
              <RevoltIcon name="lucide:wand-sparkles" />
              <span>Remix App</span>
            </button>
          )}

          <button
            className="app-context-menu__item"
            role="menuitem"
            onClick={() => handleAction(async () => {
              // Store payload for workspace section (same as navigateWithPayload does)
              const payloadData = {
                payload: {
                  action: 'openWorkspaceSafeMode',
                  appId: project.id,
                  safeMode: true,
                  fromSection: 'app',
                  timestamp: Date.now()
                },
                timestamp: Date.now(),
                sourceSection: 'app'
              };
              sessionStorage.setItem('sectionPayload_workspace', JSON.stringify(payloadData));

              // Navigate to workspace with appId
              await navigationManager.navigateToPath(`/workspace/${project.id}`);
            })}
          >
            <i className="fa-solid fa-shield-halved" />
            <span>Open Workspace In Safe Mode</span>
          </button>

          <button
            className="app-context-menu__item"
            role="menuitem"
            onClick={() => handleAction(() => onToggleFavorite(project.id))}
          >
            <i className={favoriteIcon} />
            <span>{favoriteLabel}</span>
          </button>

          {!isOwner && (
            <button
              className="app-context-menu__item app-context-menu__item--danger"
              role="menuitem"
              onClick={() => handleAction(() => onLeaveApp && onLeaveApp(project))}
            >
              <i className="fa-solid fa-arrow-right-from-bracket" />
              <span>Leave</span>
            </button>
          )}

          {isOwner && easyAppManagementEnabled && (
            <>
              <div className="app-context-menu__divider" />
              <button
                className="app-context-menu__item app-context-menu__item--danger"
                role="menuitem"
                onClick={() => handleAction(() => onDeleteApp && onDeleteApp(project))}
              >
                <i className="fa-solid fa-trash" />
                <span>Delete App</span>
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(menuTree, document.body);
}

